import { useEffect, useRef, useState } from "react";
import "./CartOrderDetails.scss";
import { Button, Card, Typography, Divider, Select, Input, Form } from "antd";
import { Captcha } from "../../../../components";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import {
  makeOrder,
  makeOrderForUnAuthUser,
} from "../../../../features/order/orderActions";
import { clearCard } from "../../../../features/card/cardSlice";
import { patchPatient } from "../../../../features/patient/patientActions";
import { forgetGuestPatient } from "../../../../features/patient/patientSlice";
import { getCaptcha } from "../../../../features/captcha/captchaActions";
import { getPromoCodes } from "../../../../features/promoCodes/promoCodeActions";
import ShippingAddress from "./ShippingAddress";
import { PickUpPoint } from "../../../../modals";
import Utils from "../../../../utils";
import cartOrderDetailsSchema from "../../../../validations/cartOrderDetailsValidation";
import { createYupValidator } from "../../../../utils/validationHelper";

const ADDRESS_ERROR = "Please add a pickup address before proceeding.";

export default function CartOrderDetails({
  token,
  isMobile,
  orderCalculation,
  openNotificationWithIcon,
  haveCartCredentials,
  formRef,
  orderLoading,
  setOrderLoading,
  selectedPromoCode,
  setSelectedPromoCode,
  unAuthenticatedPatientId,
}) {
  const orderFormRef = useRef(null);
  const { isAuthenticated } = useSelector((state) => state?.auth);
  const { patient, loading, error, unAuthenticatedRecipientName } = useSelector(
    (state) => state?.patient
  );
  const { items } = useSelector((state) => state?.card);
  const {
    codes: { results: promoCodes },
    loading: promoCodesLoading,
  } = useSelector((state) => state?.promoCodes);
  const navigate = useNavigate();
  const [pick_up_point_opened, set_pick_up_point_opened] = useState(false);
  const [addressError, setAddressError] = useState("");
  const dispatch = useDispatch();
  const [selectedShippingAddress, setSelectedShippingAddress] =
    useState(undefined);
  const [captcha, setCaptcha] = useState(null);
  const [isCaptchaLoading, setIsCaptchaLoading] = useState(false);

  const foundPromoCode = selectedPromoCode
    ? promoCodes?.find(
        (code) => code?.id?.toString() === selectedPromoCode?.value?.toString()
      )
    : null;

  const checkout = () => {
    setOrderLoading(true);
    dispatch(
      makeOrder({
        customer: patient?.id,
        phone_number: patient?.phone_number,
        order_items: items?.map((el) => ({
          menu_item: el?.menu_item,
          quantity: el?.quantity,
          variant: el?.variant,
        })),
        grand_total: orderCalculation?.grand_total,
        address:
          selectedShippingAddress && selectedShippingAddress?.id !== "current"
            ? Utils?.getDisplayAddress(selectedShippingAddress)
            : undefined,
        promo_code: selectedPromoCode ? selectedPromoCode?.value : undefined,
      })
    ).then((res) => {
      if (res) {
        setOrderLoading(false);
        if (res?.type === "makeOrder/fulfilled") {
          setSelectedPromoCode(undefined);
          dispatch(clearCard());
          setAddressError("");
          openNotificationWithIcon(
            "success",
            "Create Order",
            "Your order has been successfully created."
          );
          setTimeout(() => {
            navigate("/");
          }, 700);
        } else if (res?.type === "makeOrder/rejected") {
          if (
            typeof res?.payload === "object" &&
            !Array.isArray(res?.payload)
          ) {
            for (let key in res?.payload) {
              if (
                res?.payload[key] &&
                res?.payload[key][0] &&
                typeof res?.payload[key][0] === "string"
              ) {
                openNotificationWithIcon("error", "Error", res?.payload[key]);
                break;
              } else {
                openNotificationWithIcon(
                  "error",
                  "Error",
                  "Your order has not been created."
                );
                break;
              }
            }
          } else {
            openNotificationWithIcon(
              "error",
              "Error",
              "Your order has not been created."
            );
          }
        }
      }
    });
  };

  const reGenerateCaptcha = () => {
    setIsCaptchaLoading(true);
    dispatch(getCaptcha())?.then((res) => {
      if (res?.type === "get_captcha/fulfilled") {
        setCaptcha(res?.payload);
      }
      setIsCaptchaLoading(false);
    });
  };

  const checkoutForUnAuthUser = (params) => {
    setOrderLoading(true);
    // Who is ordering: the name typed at checkout, else (a guest id saved
    // before it was kept) the guest record's own, which is what was typed.
    const recipientName =
      unAuthenticatedRecipientName ||
      [patient?.first_name, patient?.last_name].filter(Boolean).join(" ");
    dispatch(
      makeOrderForUnAuthUser({
        customer: patient?.id,
        recipient_name: recipientName || undefined,
        phone_number: patient?.phone_number, // TODO check
        order_items: items?.map((el) => ({
          menu_item: el?.menu_item,
          quantity: el?.quantity,
          variant: el?.variant,
        })),
        grand_total: orderCalculation?.grand_total,
        address:
          selectedShippingAddress && selectedShippingAddress?.id !== "current"
            ? Utils?.getDisplayAddress(selectedShippingAddress)
            : undefined,
        promo_code: selectedPromoCode ? selectedPromoCode?.value : undefined,
        ...params,
      })
    ).then((res) => {
      if (res) {
        setOrderLoading(false);
        if (res?.type === "makeOrder_for_un_authUser/fulfilled") {
          setSelectedPromoCode(undefined);
          dispatch(clearCard());
          setAddressError("");
          orderFormRef?.current?.resetFields();
          openNotificationWithIcon(
            "success",
            "Create Order",
            "Your order has been successfully created."
          );
          setTimeout(() => {
            navigate("/");
          }, 700);
        } else if (res?.type === "makeOrder_for_un_authUser/rejected") {
          // The guest record is no longer this browser's to order for (its
          // guest_token is missing or expired, or it got a shop login since
          // checkout loaded). The order endpoint says so the way it answers
          // any unknown customer id, a 400 on `customer`; a 404 means the
          // same. Forget it, like checkout does when reading it 404s, so the
          // billing form comes back with the cart kept.
          const guestRecordRefused =
            res?.meta?.status === 404 ||
            (res?.meta?.status === 400 && Boolean(res?.payload?.customer));
          if (guestRecordRefused) {
            dispatch(forgetGuestPatient(unAuthenticatedPatientId));
            openNotificationWithIcon(
              "error",
              "Error",
              "Please enter your details again to place the order."
            );
            return;
          }
          if (
            typeof res?.payload === "object" &&
            !Array.isArray(res?.payload)
          ) {
            for (let key in res?.payload) {
              if (
                res?.payload[key] &&
                res?.payload[key][0] &&
                typeof res?.payload[key][0] === "string"
              ) {
                openNotificationWithIcon("error", key, res?.payload[key]);
                break;
              } else {
                openNotificationWithIcon(
                  "error",
                  "Error",
                  "Your order has not been created."
                );
                break;
              }
            }
          } else {
            openNotificationWithIcon(
              "error",
              "Error",
              "Your order has not been created."
            );
          }
          orderFormRef?.current?.resetFields();
          reGenerateCaptcha();
        }
      }
    });
  };

  useEffect(() => {
    dispatch(getPromoCodes({ page: 1, per_page: 100, status: "active" }));
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated && haveCartCredentials) {
      setIsCaptchaLoading(true);
      dispatch(getCaptcha())?.then((res) => {
        if (res?.type === "get_captcha/fulfilled") {
          setCaptcha(res?.payload);
        }
        setIsCaptchaLoading(false);
      });
    }
  }, [dispatch, isAuthenticated, haveCartCredentials]);

  const getPayementDetailPrice = (price) => {
    if (!isNaN(price)) {
      return Utils?.roundNumber(price);
    }
  };

  const showTaxes = (taxes) => {
    const res = [];
    let i = 0;
    for (let key in taxes) {
      if (key !== "discount_total" && key !== "grand_total") {
        const formattedKey = key
          ?.split("_") // Split key by underscores
          ?.map(
            (word) =>
              word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase()
          ) // Capitalize each word
          ?.join(" ");
        if (key === "promo_code") {
          res?.push(
            <div className="cartOrderDetails-cont__textWrapper" key={i}>
              <Typography.Text
                className="cartOrderDetails-cont__text"
                style={{
                  color: token?.Typography?.colorPrimaryText,
                }}>
                {formattedKey}
              </Typography.Text>
              <Typography.Text
                className="cartOrderDetails-cont__title"
                style={{
                  color: token?.Typography?.colorPrimaryText,
                }}>
                {taxes[key] || taxes[key] === 0
                  ? `${
                      taxes[key] === 0
                        ? getPayementDetailPrice(taxes[key])
                        : "-" + getPayementDetailPrice(taxes[key])
                    }`
                  : "$0"}
              </Typography.Text>
            </div>
          );
        } else {
          res?.push(
            <div className="cartOrderDetails-cont__textWrapper" key={i}>
              <Typography.Text
                className="cartOrderDetails-cont__text"
                style={{
                  color: token?.Typography?.mainColorText,
                }}>
                {formattedKey}
              </Typography.Text>
              <Typography.Text
                className="cartOrderDetails-cont__title"
                style={{
                  color: token?.Typography?.mainColorText,
                }}>
                {taxes[key] || taxes[key] === 0
                  ? getPayementDetailPrice(taxes[key])
                  : "$0"}
              </Typography.Text>
            </div>
          );
        }

        i += 1;
      }
    }
    return res;
  };

  return (
    <>
      <Card
        className="cartOrderDetails-cont"
        style={
          haveCartCredentials
            ? {
                border: "none",
                backgroundColor: token?.Card?.colorBgCard,
              }
            : {
                height: "fit-content",
                border: "none",
                backgroundColor: token?.Card?.colorBgCard,
              }
        }
        styles={{
          body: isMobile
            ? {
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                padding: "16px",
              }
            : {
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                padding: "32px",
              },
        }}>
        <div className="cartOrderDetails-cont__promoCodeWrapper">
          <Typography.Text
            className="cartOrderDetails-cont__text"
            style={{
              color: token?.Typography?.mainColorText,
            }}>
            Promo Code
          </Typography.Text>
          <Select
            className="cartOrderDetails-cont__promoCodeWrapper__promoInp"
            placeholder="Promo Code"
            value={selectedPromoCode ? selectedPromoCode : undefined}
            options={promoCodes?.map((promoCode) => ({
              label: promoCode?.code ? promoCode?.code : "",
              value: promoCode?.id ? promoCode?.id : "",
            }))}
            allowClear
            onClear={() => {
              setSelectedPromoCode(undefined);
            }}
            onChange={(_, val) => {
              setSelectedPromoCode(val);
            }}
            disabled={orderLoading}
            loading={promoCodesLoading}
          />
          {selectedPromoCode ? (
            <Typography.Text
              className="cartOrderDetails-cont__text"
              style={{
                color: token?.Typography?.colorPrimaryText,
                fontSize: "14px",
              }}
              ellipsis={{
                tooltip: {
                  title: foundPromoCode?.description
                    ? foundPromoCode?.description
                    : "No Description",
                },
              }}>
              {foundPromoCode?.description
                ? foundPromoCode?.description
                : "No Description"}
            </Typography.Text>
          ) : null}
        </div>
        <Divider
          orientation="horizontal"
          className="cartOrderDetails-cont__divider"
          style={{
            borderColor: token?.Layout?.footerBorderTopColor,
          }}
        />
        <div className="cartOrderDetails-cont__keyValPairWrapper">
          <Typography.Text
            className="cartOrderDetails-cont__text"
            style={{
              color: token?.Typography?.mainColorText,
            }}>
            Order Summary
          </Typography.Text>
          {showTaxes(orderCalculation)}
        </div>
        <Divider
          orientation="horizontal"
          className="cartOrderDetails-cont__divider"
          style={{
            borderColor: token?.Layout?.footerBorderTopColor,
          }}
        />
        {haveCartCredentials ? (
          <ShippingAddress
            token={token}
            openNotificationWithIcon={openNotificationWithIcon}
            addressError={addressError}
            selectedShippingAddress={selectedShippingAddress}
            setSelectedShippingAddress={setSelectedShippingAddress}
            set_pick_up_point_opened={set_pick_up_point_opened}
          />
        ) : null}
        {haveCartCredentials ? (
          <Divider
            orientation="horizontal"
            className="cartOrderDetails-cont__divider"
            style={{
              borderColor: token?.Layout?.footerBorderTopColor,
            }}
          />
        ) : null}
        {captcha && !isAuthenticated && haveCartCredentials ? (
          <>
            <div className="cartOrderDetails-cont__captchaWrapper">
              <Captcha
                captcha={captcha}
                isLoading={isCaptchaLoading}
                setCaptcha={setCaptcha}
                setIsLoading={setIsCaptchaLoading}
              />
              <Form
                className="cartOrderDetails-cont__captchaWrapper__form"
                ref={orderFormRef}
                onFinish={(values) => {
                  checkoutForUnAuthUser({
                    captcha_value: values?.captcha_value
                      ? values?.captcha_value
                      : null,
                    captcha_key: captcha?.captcha_key
                      ? captcha?.captcha_key
                      : null,
                  });
                }}>
                <Form.Item
                  className="cartOrderDetails-cont__captchaWrapper__form__formItem"
                  name="captcha_value"
                  rules={[
                    {
                      required: true,
                      validator: createYupValidator(
                        cartOrderDetailsSchema,
                        "captcha_value"
                      ),
                    },
                  ]}
                  colon={false}>
                  <Input
                    style={{
                      backgroundColor: token?.Card?.colorBgCard,
                    }}
                    placeholder="Captcha value"
                    disabled={orderLoading || !items?.length}
                  />
                </Form.Item>
              </Form>
            </div>
            <Divider
              orientation="horizontal"
              className="cartOrderDetails-cont__divider"
              style={{
                borderColor: token?.Layout?.footerBorderTopColor,
              }}
            />
          </>
        ) : null}
        <div className="cartOrderDetails-cont__textWrapper">
          <Typography.Text
            className="cartOrderDetails-cont__text"
            style={{
              color: token?.Typography?.mainColorText,
            }}>
            Total
          </Typography.Text>
          <Typography.Text
            className="cartOrderDetails-cont__title"
            style={{
              color: token?.Typography?.mainColorText,
            }}>
            {orderCalculation?.grand_total ||
            orderCalculation?.grand_total === 0
              ? getPayementDetailPrice(orderCalculation?.grand_total)
              : "$0"}
          </Typography.Text>
        </div>
        {!isAuthenticated && !unAuthenticatedPatientId ? (
          <Button
            type="primary"
            block={isMobile}
            loading={loading}
            disabled={error ? Object.keys(error)?.length !== 0 : false}
            onClick={() => {
              formRef?.current?.submit();
            }}>
            Next
          </Button>
        ) : (
          <Button
            onClick={() => {
              if (!isAuthenticated) {
                orderFormRef?.current?.submit();
              } else {
                if (patient?.address) {
                  checkout();
                } else {
                  setAddressError(ADDRESS_ERROR);
                }
              }
            }}
            type="primary"
            disabled={!items?.length || addressError}
            loading={orderLoading}>
            Proceed To Checkout
          </Button>
        )}
      </Card>
      <PickUpPoint
        title="Pick Up Point"
        isOpened={pick_up_point_opened}
        onCancel={() => {
          set_pick_up_point_opened(false);
        }}
        onOk={(params) => {
          dispatch(patchPatient({ ...params, id: patient?.id }))?.then(
            (res) => {
              if (res?.type === "patch_patient/fulfilled") {
                setAddressError("");
              }
            }
          );
        }}
      />
    </>
  );
}
